import { useEffect, useMemo, useState } from "react";
import { apiFetch } from "../lib/api";
import { toast } from "react-toastify";

type Company = {
  id: number;
  name: string;
  address?: string;
  gstin?: string;
  email?: string;
  notes?: string;
};

export default function CompanyPage() {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [form, setForm] = useState({
    id: null as number | null,
    name: "",
    address: "",
    gstin: "",
    email: "",
    notes: "",
  });
  const [status, setStatus] = useState<string | null>(null);
  const [companySearch, setCompanySearch] = useState<string>("");

  const loadCompanies = () => {
    apiFetch("/api/companies")
      .then((res) => res.json())
      .then((data) => setCompanies(Array.isArray(data) ? data : []))
      .catch(() => setCompanies([]));
  };

  useEffect(() => {
    loadCompanies();
  }, []);

  const filteredCompanies = useMemo(() => {
    const term = companySearch.trim().toLowerCase();
    if (!term) {
      return companies;
    }
    return companies.filter((company) =>
      company.name.toLowerCase().includes(term)
    );
  }, [companies, companySearch]);

  const handleChange = (
    field: keyof typeof form,
    value: string
  ) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setStatus("Saving company...");

    try {
      const response = await apiFetch(
        form.id ? `/api/companies/${form.id}` : "/api/companies",
        {
        method: form.id ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.error || "Failed to save");
      }

      setForm({
        id: null,
        name: "",
        address: "",
        gstin: "",
        email: "",
        notes: "",
      });
      const message = form.id ? "Company updated." : "Company saved.";
      setStatus(message);
      toast.success(message);
      loadCompanies();
    } catch (error: any) {
      setStatus(error?.message || "Failed to save company.");
      toast.error(error?.message || "Failed to save company.");
    }
  };

  const handleEdit = (company: Company) => {
    setForm({
      id: company.id,
      name: company.name || "",
      address: company.address || "",
      gstin: company.gstin || "",
      email: company.email || "",
      notes: company.notes || "",
    });
    setStatus(null);
  };

  const handleDuplicate = (company: Company) => {
    setForm({
      id: null,
      name: company.name || "",
      address: company.address || "",
      gstin: company.gstin || "",
      email: company.email || "",
      notes: company.notes || "",
    });
    setStatus("Company copied. Save to create a new entry.");
    toast.info("Company copied. Save to create a new entry.");
  };

  const handleReset = () => {
    setForm({
      id: null,
      name: "",
      address: "",
      gstin: "",
      email: "",
      notes: "",
    });
    setStatus(null);
  };

  return (
    <section className="grid gap-6">
      <div className="card p-6">
        <h2 className="font-display text-xl font-semibold text-ink">
          Company Details
        </h2>
        <p className="text-sm text-slate-500">
          Add and manage the companies you invoice.
        </p>
      </div>

      <div className="grid items-start gap-6 md:grid-cols-[1.15fr_1fr]">
        <form className="card grid gap-4 p-6" onSubmit={handleSubmit}>
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-600">
              {form.id ? "Edit Company" : "Add Company"}
            </h3>
            {form.id && (
              <button type="button" className="button-secondary" onClick={handleReset}>
                New Company
              </button>
            )}
          </div>

          <div className="grid gap-4 md:grid-cols-2">
          <div>
            <label className="text-sm font-semibold text-slate-600">Name</label>
            <input
              className="input"
              value={form.name}
              onChange={(e) => handleChange("name", e.target.value)}
              required
            />
          </div>
          <div>
            <label className="text-sm font-semibold text-slate-600">GSTIN</label>
            <input
              className="input"
              value={form.gstin}
              onChange={(e) => handleChange("gstin", e.target.value)}
            />
          </div>
          <div>
            <label className="text-sm font-semibold text-slate-600">Email</label>
            <input
              className="input"
              type="email"
              value={form.email}
              onChange={(e) => handleChange("email", e.target.value)}
            />
          </div>
        </div>

        <div>
          <label className="text-sm font-semibold text-slate-600">Address</label>
          <textarea
            className="input min-h-[90px]"
            value={form.address}
            onChange={(e) => handleChange("address", e.target.value)}
          />
        </div>

        <div>
          <label className="text-sm font-semibold text-slate-600">Notes</label>
          <textarea
            className="input min-h-[90px]"
            value={form.notes}
            onChange={(e) => handleChange("notes", e.target.value)}
          />
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <button className="button" type="submit">
            {form.id ? "Update Company" : "Save Company"}
          </button>
          {form.id && (
            <button type="button" className="button-secondary" onClick={handleReset}>
              Cancel Edit
            </button>
          )}
          {status && <span className="text-sm text-slate-500">{status}</span>}
        </div>
        </form>

        <div className="card p-0 max-h-[calc(100vh-220px)] overflow-hidden lg:self-start">
          <div className="sticky top-0 z-10 border-b border-slate-200 bg-white/95 p-6 backdrop-blur">
            <h3 className="text-sm font-semibold text-slate-600">Saved Companies</h3>
            <div className="mt-3 grid gap-2">
              <label className="text-xs font-semibold text-slate-500">
                Search companies
              </label>
              <input
                className="input"
                placeholder="Search by company..."
                value={companySearch}
                onChange={(e) => setCompanySearch(e.target.value)}
              />
              {companies.length > 0 &&
                filteredCompanies.length === 0 &&
                companySearch.trim() !== "" && (
                  <p className="text-xs text-slate-500">
                    No companies match "{companySearch.trim()}".
                  </p>
                )}
            </div>
          </div>

          <div className="max-h-[calc(100vh-220px-140px)] overflow-auto p-6">
            {filteredCompanies.length === 0 ? (
              <p className="text-sm text-slate-500">
                {companies.length === 0
                  ? "No companies yet."
                  : "No companies match this search."}
              </p>
            ) : (
              <ul className="grid gap-3">
                {filteredCompanies.map((company) => (
                  <li
                    key={company.id}
                    className="rounded-xl border border-slate-200 bg-white p-4"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-sm font-semibold text-ink">{company.name}</p>
                        <p className="text-xs text-slate-500">
                          <span className="font-semibold text-slate-600">GSTIN:</span>{" "}
                          {company.gstin || "Not provided"}
                        </p>
                        <p className="text-xs text-slate-500">
                          {company.email || "No email"}
                        </p>
                        {company.address && (
                          <p className="text-xs text-slate-500 mt-1">
                            {company.address}
                          </p>
                        )}
                      </div>
                      <div className="flex flex-col gap-2">
                        <button
                          type="button"
                          className="button-secondary"
                          onClick={() => handleEdit(company)}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="button-secondary"
                          onClick={() => handleDuplicate(company)}
                        >
                          Duplicate
                        </button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
